import { useState } from 'react';
import { Sparkles, Search, Command } from 'lucide-react';
import AppGrid from '../components/dashboard/AppGrid';
import QuickStats from '../components/dashboard/QuickStats';
import ActivityFeed from '../components/dashboard/ActivityFeed';
import CommandPalette from '../components/dashboard/CommandPalette';
import NotificationCenter from '../components/dashboard/NotificationCenter';
import UserMenu from '../components/dashboard/UserMenu';
import SupportChat from '../components/dashboard/SupportChat';
import CategoryFilter from '../components/CategoryFilter';
import { AppCategory } from '../types';
import { useAppStore } from '../store/appStore';

export default function Dashboard() {
  const { toggleCommandPalette } = useAppStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<AppCategory | 'All'>('All');

  const categories: (AppCategory | 'All')[] = [
    'All',
    'ai-tools',
    'client-tools',
    'internal',
    'mcp-servers',
    'automation'
  ];

  return (
    <div className="min-h-screen bg-recipe-bg relative">
      {/* Animated Background */}
      <div className="bg-animation" />
      <div className="grid-overlay" />

      {/* Header */}
      <header className="sticky top-0 z-40 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-recipe-bg-card border border-recipe-border flex items-center justify-center">
                  <span className="text-xl font-bold font-display text-gradient">R</span>
                </div>
                <h1 className="text-xl font-bold font-display text-gradient tracking-wider">RCPE LAB</h1>
              </div>
              <span className="team-badge">Team Hub</span>
            </div>

            {/* Search & Actions */}
            <div className="flex items-center gap-3">
              {/* Command Palette Trigger */}
              <button
                onClick={toggleCommandPalette}
                className="hidden md:flex items-center gap-2 px-3 py-2 bg-recipe-bg border border-recipe-border rounded-lg hover:border-recipe-accent transition-colors"
              >
                <Search className="w-4 h-4 text-recipe-text-muted" />
                <span className="text-sm text-recipe-text-muted">Find anything...</span>
                <kbd className="px-2 py-0.5 bg-recipe-card border border-recipe-border rounded text-xs text-recipe-text-muted font-mono">
                  ⌘K
                </kbd>
              </button>

              {/* Mobile Search Button */}
              <button
                onClick={toggleCommandPalette}
                className="md:hidden p-2 rounded-lg hover:bg-recipe-bg transition-colors"
              >
                <Search className="w-5 h-5 text-recipe-text" />
              </button>

              {/* Notification Center */}
              <NotificationCenter />

              {/* User Menu */}
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold font-display text-gradient mb-2">Dashboard</h2>
          <p className="text-recipe-text-muted font-tech">Your Team's Workspace</p>
        </div>

        {/* Quick Stats */}
        <section className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="section-number">00</span>
            <h3 className="text-lg font-semibold font-display text-recipe-text">At a Glance</h3>
          </div>
          <QuickStats />
        </section>

        {/* Search and Filter Bar */}
        <section className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            {/* Search Input */}
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-recipe-text-muted" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search for tools..."
                className="w-full pl-10 pr-4 py-2.5 bg-recipe-card border border-recipe-border rounded-lg text-sm text-recipe-text placeholder-recipe-text-muted focus:outline-none focus:ring-2 focus:ring-recipe-accent/50"
              />
            </div>

            {/* Category Filter */}
            <div className="w-full sm:w-auto">
              <CategoryFilter
                categories={categories}
                selected={selectedCategory}
                onChange={setSelectedCategory}
              />
            </div>
          </div>
        </section>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Apps Grid - Takes 2 columns */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <span className="section-number">01</span>
              <h3 className="text-lg font-semibold font-display text-recipe-text">Your Tools</h3>
            </div>
            <AppGrid searchTerm={searchTerm} selectedCategory={selectedCategory} />
          </div>

          {/* Activity Feed - Takes 1 column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <span className="section-number">02</span>
              <h3 className="text-lg font-semibold font-display text-recipe-text">What's Happening</h3>
            </div>
            <ActivityFeed />
          </div>
        </div>
      </main>

      {/* Command Palette */}
      <CommandPalette />

      {/* AI Support Chat */}
      <SupportChat />

      {/* Footer */}
      <footer className="border-t border-recipe-border mt-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="font-display text-sm text-gradient tracking-wider">RECIPE LABS</span>
              <span className="text-recipe-text-muted text-sm font-tech">v1.0.0</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-recipe-text-muted font-body">
              <a
                href="https://guide.madebyrecipe.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-recipe-accent transition-colors duration-200"
              >
                Documentation
              </a>
              <span className="text-recipe-border">|</span>
              <a
                href="https://madebyrecipe.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-recipe-accent transition-colors duration-200"
              >
                madebyrecipe.com
              </a>
              <span className="text-recipe-border">|</span>
              <button
                onClick={toggleCommandPalette}
                className="flex items-center gap-1 hover:text-recipe-accent transition-colors duration-200"
              >
                <Command className="w-3 h-3" />
                <span className="font-tech">CMD</span>
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
